import { takeLatest, put } from 'redux-saga/effects';
import performanceSlice from 'store/modules/performance';
import uiSlice from 'store/modules/ui';
import performance from 'api/graphql/performance';
import { cleanApiDataResponse, createSnack, getUserFriendlyError } from 'common/utils/general';
import createPerformanceList from 'store/sagas/hof/performance';
import i18n from 'i18n';

/**
 * Create performance saga
 *
 * @param {*} action
 */
function* create(action) {
  try {
    const snack = createSnack({
      key: 'performance-create-notification-success',
      variant: 'success',
      message: i18n.t('performance.create', { ns: 'notifications' })
    });

    const { payload } = action;
    yield performance.create(payload);
    yield put(performanceSlice.actions.createSuccess());
    yield put(uiSlice.actions.enqueueSnackbar(snack));
  } catch (ex) {
    const message = getUserFriendlyError(ex);
    yield put(performanceSlice.actions.createError(message));
  }
}

/**
 * Create performance with dynamic exercises saga
 *
 * @param {*} action
 */
function* createWithExercises(action) {
  try {
    const snack = createSnack({
      key: 'performance-create-exercises-notification-success',
      variant: 'success',
      message: i18n.t('performance.create', { ns: 'notifications' })
    });

    const { payload } = action;
    const response = yield performance.createWithExercises(payload);
    const {
      data: { createPerformanceWithExercises: result }
    } = response;

    yield put(
      performanceSlice.actions.createWithExercisesSuccess({
        performance: result.performance,
        achievements: result.new_achievements || []
      })
    );
    yield put(uiSlice.actions.enqueueSnackbar(snack));

    // Show achievement notifications if any
    const achievements = result.new_achievements || [];
    if (achievements.length > 0) {
      for (let i = 0; i < achievements.length; i += 1) {
        const achievementSnack = createSnack({
          key: `achievement-${achievements[i].id}`,
          variant: 'info',
          message: achievements[i].localized_name
        });
        yield put(uiSlice.actions.enqueueSnackbar(achievementSnack));
      }
    }
  } catch (ex) {
    const message = getUserFriendlyError(ex);
    yield put(performanceSlice.actions.createWithExercisesError(message));
  }
}

/**
 * Find performance saga
 *
 * @param {*} action
 */
function* find(action) {
  try {
    const response = yield performance.find(action.payload);
    const {
      data: { performance: currentPerformance }
    } = response;

    yield put(performanceSlice.actions.findSuccess(currentPerformance));
  } catch (ex) {
    const message = getUserFriendlyError(ex);
    yield put(performanceSlice.actions.findError(message));
  }
}

/**
 * Get performance history saga
 *
 * @param {*} action
 */
function* getHistory(action) {
  try {
    const response = yield performance.history(action.payload);

    const {
      data: { performanceHistory }
    } = response;

    yield put(performanceSlice.actions.getHistorySuccess(cleanApiDataResponse(performanceHistory)));
  } catch (ex) {
    const message = getUserFriendlyError(ex);
    yield put(performanceSlice.actions.getHistoryError(message));
  }
}

/**
 * Get latest performance saga
 *
 * @param {*} action
 */
function* getLatest(action) {
  try {
    const response = yield performance.getLatest(Number(action.payload));

    const {
      data: { latestPerformance }
    } = response;

    yield put(performanceSlice.actions.getLatestSuccess(latestPerformance));
  } catch (ex) {
    const message = getUserFriendlyError(ex);
    yield put(performanceSlice.actions.getLatestError(message));
  }
}

/**
 * Remove performance saga
 *
 * @param {*} action
 */
function* remove(action) {
  try {
    const {
      data: { deletePerformances: removedPerformance }
    } = yield performance.remove(action.payload);

    const snack = createSnack({
      key: 'remove-performance-notification-success',
      variant: 'success',
      message: i18n.t('performance.delete', {
        count: removedPerformance.length,
        ns: 'notifications'
      })
    });

    yield put(performanceSlice.actions.removeSuccess());
    yield put(uiSlice.actions.enqueueSnackbar(snack));
  } catch (ex) {
    const message = getUserFriendlyError(ex);
    yield put(performanceSlice.actions.removeError(message));
  }
}

/**
 * Restore performance saga
 *
 * @param {*} action
 */
function* restore(action) {
  try {
    const {
      data: { restorePerformances: restoredPerformance }
    } = yield performance.restore(action.payload);

    const snack = createSnack({
      key: 'restore-performance-notification-success',
      variant: 'success',
      message: i18n.t('performance.restore', {
        count: restoredPerformance.length,
        ns: 'notifications'
      })
    });

    yield put(performanceSlice.actions.restoreSuccess());
    yield put(uiSlice.actions.enqueueSnackbar(snack));
  } catch (ex) {
    const message = getUserFriendlyError(ex);
    yield put(performanceSlice.actions.restoreError(message));
  }
}

/**
 * Top ranked performance saga
 *
 * @param {*} action
 */
function* topRanked(action) {
  try {
    const response = yield performance.topRanked(action.payload);

    const {
      data: { topRankedPerformances: topRankedObj }
    } = response;

    yield put(performanceSlice.actions.topRankedSuccess(topRankedObj));
  } catch (ex) {
    const message = getUserFriendlyError(ex);
    yield put(performanceSlice.actions.topRankedError(message));
  }
}

/**
 * Update performance saga
 *
 * @param {*} action
 */
function* update(action) {
  try {
    const snack = createSnack({
      key: 'update-performance-notification-success',
      variant: 'success',
      message: i18n.t('performance.update', { ns: 'notifications' })
    });

    yield performance.update(action.payload);

    yield put(performanceSlice.actions.updateSuccess());
    yield put(uiSlice.actions.enqueueSnackbar(snack));
  } catch (ex) {
    const message = getUserFriendlyError(ex);
    yield put(performanceSlice.actions.updateError(message));
  }
}

/**
 * Create dynamic performance saga
 *
 * @param {*} action
 */
function* createDynamic(action) {
  try {
    const snack = createSnack({
      key: 'dynamic-performance-create-success',
      variant: 'success',
      message: 'Rendimiento dinámico creado exitosamente'
    });

    const { payload } = action;
    const response = yield performance.createDynamicPerformance(payload);
    const {
      data: { createDynamicPerformance: result }
    } = response;

    yield put(performanceSlice.actions.createDynamicSuccess(result));
    yield put(uiSlice.actions.enqueueSnackbar(snack));
  } catch (ex) {
    const message = getUserFriendlyError(ex);
    yield put(performanceSlice.actions.createDynamicError(message));
  }
}

/**
 * Create dynamic performances in batch (one per exercise card)
 *
 * @param {*} action
 */
function* createDynamicBatch(action) {
  try {
    const { exercises } = action.payload;

    // Call createDynamicPerformance for each exercise sequentially
    for (let i = 0; i < exercises.length; i += 1) {
      yield performance.createDynamicPerformance(exercises[i]);
    }

    const snack = createSnack({
      key: 'dynamic-performance-batch-create-success',
      variant: 'success',
      message: i18n.t('performance.create', { ns: 'notifications' })
    });

    yield put(performanceSlice.actions.createDynamicBatchSuccess());
    yield put(uiSlice.actions.enqueueSnackbar(snack));
  } catch (ex) {
    const message = getUserFriendlyError(ex);
    yield put(performanceSlice.actions.createDynamicBatchError(message));
  }
}

/**
 * Update dynamic performance saga
 *
 * @param {*} action
 */
function* updateDynamic(action) {
  try {
    const snack = createSnack({
      key: 'dynamic-performance-update-success',
      variant: 'success',
      message: 'Rendimiento dinámico actualizado exitosamente'
    });

    const { id, input } = action.payload;
    const response = yield performance.updateDynamicPerformance(id, input);
    const {
      data: { updateDynamicPerformance: result }
    } = response;

    yield put(performanceSlice.actions.updateDynamicSuccess(result));
    yield put(uiSlice.actions.enqueueSnackbar(snack));
  } catch (ex) {
    const message = getUserFriendlyError(ex);
    yield put(performanceSlice.actions.updateDynamicError(message));
  }
}

/**
 * Delete dynamic performance saga
 *
 * @param {*} action
 */
function* deleteDynamic(action) {
  try {
    const snack = createSnack({
      key: 'dynamic-performance-delete-success',
      variant: 'success',
      message: 'Rendimiento dinámico eliminado exitosamente'
    });

    const { payload: id } = action;
    yield performance.deleteDynamicPerformance(id);

    yield put(performanceSlice.actions.deleteDynamicSuccess());
    yield put(uiSlice.actions.enqueueSnackbar(snack));
  } catch (ex) {
    const message = getUserFriendlyError(ex);
    yield put(performanceSlice.actions.deleteDynamicError(message));
  }
}

/**
 * List dynamic performances saga
 *
 * @param {*} action
 */
function* listDynamic(action) {
  try {
    const { payload: filters } = action;
    const response = yield performance.getDynamicPerformances(filters);
    const {
      data: { dynamicPerformances }
    } = response;

    yield put(performanceSlice.actions.listDynamicSuccess(dynamicPerformances));
  } catch (ex) {
    const message = getUserFriendlyError(ex);
    yield put(performanceSlice.actions.listDynamicError(message));
  }
}

/**
 * Get single dynamic performance saga
 *
 * @param {*} action
 */
function* getDynamic(action) {
  try {
    const { payload: id } = action;
    const response = yield performance.getDynamicPerformance(id);
    const {
      data: { dynamicPerformance }
    } = response;

    yield put(performanceSlice.actions.getDynamicSuccess(dynamicPerformance));
  } catch (ex) {
    const message = getUserFriendlyError(ex);
    yield put(performanceSlice.actions.getDynamicError(message));
  }
}

/**
 * Get dynamic performances grouped by exercise saga
 *
 * @param {*} action
 */
function* getDynamicByExercise(action) {
  try {
    const { payload: clientId } = action;
    const response = yield performance.getClientDynamicPerformancesByExercise(clientId);
    const {
      data: { clientDynamicPerformancesByExercise }
    } = response;

    yield put(performanceSlice.actions.getDynamicByExerciseSuccess(clientDynamicPerformancesByExercise));
  } catch (ex) {
    const message = getUserFriendlyError(ex);
    yield put(performanceSlice.actions.getDynamicByExerciseError(message));
  }
}

/**
 * Watch performance
 *
 * @export
 */
export default function* watchPerformance() {
  yield takeLatest(performanceSlice.actions.create, create);
  yield takeLatest(performanceSlice.actions.createWithExercises, createWithExercises);
  yield takeLatest(performanceSlice.actions.find, find);
  yield takeLatest(performanceSlice.actions.getHistory, getHistory);
  yield takeLatest(performanceSlice.actions.getLatest, getLatest);
  yield takeLatest(
    performanceSlice.actions.list,
    createPerformanceList(performanceSlice.actions.listSuccess, performanceSlice.actions.listError)
  );
  yield takeLatest(
    performanceSlice.actions.listIncomplete,
    createPerformanceList(
      performanceSlice.actions.listIncompleteSuccess,
      performanceSlice.actions.listIncompleteError
    )
  );
  yield takeLatest(performanceSlice.actions.remove, remove);
  yield takeLatest(performanceSlice.actions.restore, restore);
  yield takeLatest(performanceSlice.actions.topRanked, topRanked);
  yield takeLatest(performanceSlice.actions.update, update);
  // Dynamic performance sagas
  yield takeLatest(performanceSlice.actions.createDynamic, createDynamic);
  yield takeLatest(performanceSlice.actions.createDynamicBatch, createDynamicBatch);
  yield takeLatest(performanceSlice.actions.updateDynamic, updateDynamic);
  yield takeLatest(performanceSlice.actions.deleteDynamic, deleteDynamic);
  yield takeLatest(performanceSlice.actions.listDynamic, listDynamic);
  yield takeLatest(performanceSlice.actions.getDynamic, getDynamic);
  yield takeLatest(performanceSlice.actions.getDynamicByExercise, getDynamicByExercise);
}
